import classNames from 'classnames';
import { useMemo } from 'react';
import useMobileScreenDetection from 'src/hooks/useMobileScreenDetection';
import IconLib from './IconLib';
import IconPath, { type IconPathProps } from './IconPath';
import { type IconType, SIZE_TYPES, type SizePropType, type SizeType } from './types';

const SIZE_DIMENSIONS: Record<SizeType | string, string> = {
  '2xl': '24',
  xl: '20',
  lg: '16',
  md: '12',
} as const;

const ICON_VIEWBOXES: Record<string, string> = {
  chat: '0 0 38 40',
  attachment: '0 0 17 18',
  'sent-arrow': '0 0 18 18',
  home: '0 0 18 18',
  technician: '0 0 18 18',
  vendor: '0 0 18 18',
  'chevron-down-sm': '0 0 18 18',
  'message-check': '0 0 20 20',
  upload: '0 0 16 16',
  property: '-4 -4 24 24',
} as const;

type Props = Omit<IconPathProps, 'path' | 'isSizeType' | 'size'> & {
  className?: string;
  size?: SizePropType | [SizePropType, SizePropType] | [SizePropType, SizePropType, SizePropType];
  testId?: string;
  type: IconType;
};

const Icon: React.FC<Props> = (props) => {
  const { className = '', size = '2xl', testId = '', type, ...pathProps } = props;
  const { isMobile, isMobileStrict } = useMobileScreenDetection();

  const path: string | string[] = IconLib[type];

  const sizeValue: SizePropType = useMemo(() => {
    if (Array.isArray(size)) {
      if (!isMobile) {
        return size[0];
      }
      if (isMobileStrict && size.length === 3) {
        return size[2];
      }
      return size[1];
    }
    return size as SizePropType;
  }, [isMobile, isMobileStrict, size]);

  const isSizeType: boolean = useMemo(
    () => SIZE_TYPES.includes(sizeValue as SizeType),
    [sizeValue],
  );

  const viewBox = ICON_VIEWBOXES[type] || '0 0 24 24';

  return (
    <svg
      className={classNames('icon', className)}
      data-testid={testId || `${type}-icon`}
      xmlns="http://www.w3.org/2000/svg"
      width={isSizeType ? SIZE_DIMENSIONS[sizeValue] : sizeValue}
      height={isSizeType ? SIZE_DIMENSIONS[sizeValue] : sizeValue}
      viewBox={viewBox}
      fill="none"
    >
      {Array.isArray(path) ? (
        path.map((d: string, index: number) => (
          <IconPath
            key={`${d}-${index}`}
            isSizeType={isSizeType}
            path={IconLib[type][index]}
            size={sizeValue}
            {...pathProps}
          />
        ))
      ) : (
        <IconPath
          isSizeType={isSizeType}
          path={IconLib[type] as string}
          size={sizeValue}
          {...pathProps}
        />
      )}
    </svg>
  );
};

export default Icon;
